import { Injectable } from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { select, Store } from "@ngrx/store";
import { RegistrationFeatureState } from "@obg/common.registration";
import { BrandMigration, SMART_ID_LOGIN_DIALOG_ID } from "@obg/mat.ui.common";
import { batchActions } from "@obg/ngrx-utils";
import {
	CommonState,
	CookieConsentSelector,
	DataProtectionConfigService,
	FraudPreventionService,
	GroupIBFraudPreventionService,
	KycConfig,
	MarketingSettingsAction,
	MarketingSettingsSelector,
	MarketingSettingsWithTokenRequest,
	MultiSessionLimitSelector,
	TwoFactorAuthConfigService,
	TwoFactorAuthSelector,
	TwoFactorChallengeDetails,
	TwoFactorChallengeType,
} from "@obg/ngx.common";
import {
	AUTH_MESSAGE_CODES,
	AUTH_MESSAGE_CONTEXT,
	AuthTransactionConfigService,
	AuthTransactionSelector,
	CORE_ROUTE,
	CovidMeasuresConfigService,
	CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT,
	CustomerDataNotConfirmedResponse,
	CustomerManagementAction,
	CustomerManagementSelector,
	DepositLimitConfigService,
	DgaAuthTransactionSelector,
	HIDE_GROUP_CONSENT_COUNTRY_OF_REGISTRATION,
	Jurisdiction,
	JurisdictionSelector,
	LatestTCandPPNotAcceptedLoginResponseData,
	LatestTCandPPNotAcceptedResponse,
	Message,
	MessageAction,
	MessageService,
	MessageType,
	MultiDepositLimitSelector,
	RESPONSIBLE_GAMING_MESSAGE_CONTEXT,
	SESSION_LOGIN_EXPIRED_CODES,
	SessionAction,
	SessionLimitExpiredMessage,
	SessionSelector,
	SmartIdAuthTransactionSelector,
	VerificationStatus,
	WalletAction,
	WalletSelector,
} from "@obg/ngx.core";
import { LoginWithAuthenticationToken } from "@obg/ngx.core/src/auth/auth.model";
import { TranslationService } from "@obg/ngx.core.content";
import { CurrencyService, DateFormat, DateService, FeatureFlagService } from "@obg/ngx.global";
import { RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { last } from "lodash-es";
import { combineLatest, EMPTY, Observable } from "rxjs";
import { filter, map, pairwise, startWith, switchMap, take, tap, withLatestFrom } from "rxjs/operators";

import { LoginFeatureState, SuspiciousLoginSelector } from "../../sdk";

import { YEAR_IN_MINUTES } from "./login-ui.const";
import { DgaLoginMessages, SgaLoginMessages } from "./login-ui.model";

@Injectable({ providedIn: "root" })
export class LoginUiFlowService {
	constructor(
		private store: Store<LoginFeatureState & RegistrationFeatureState & CommonState>,
		private sessionSelector: SessionSelector,
		private authTransactionConfig: AuthTransactionConfigService,
		private sessionAction: SessionAction,
		private currencyService: CurrencyService,
		private customerManagementAction: CustomerManagementAction,
		private messageService: MessageService,
		private marketingSettingsSelector: MarketingSettingsSelector,
		private dataProtectionConfigService: DataProtectionConfigService,
		private authTransactionSelector: AuthTransactionSelector,
		private dgaAuthTransactionSelector: DgaAuthTransactionSelector,
		private multiDepositLimitSelector: MultiDepositLimitSelector,
		private walletSelector: WalletSelector,
		private walletAction: WalletAction,
		private fraudPreventionService: FraudPreventionService,
		private groupIBFraudPreventionService: GroupIBFraudPreventionService,
		private translationService: TranslationService,
		private featureFlagService: FeatureFlagService,
		private messageAction: MessageAction,
		private dateService: DateService,
		private marketingSettingsAction: MarketingSettingsAction,
		private cookieConsentSelector: CookieConsentSelector,
		private depositLimitConfig: DepositLimitConfigService,
		private multiSessionLimitSelector: MultiSessionLimitSelector,
		private covidMeasuresConfig: CovidMeasuresConfigService,
		private twoFactorAuthConfigService: TwoFactorAuthConfigService,
		private kycConfig: KycConfig,
		private matDialog: MatDialog
	) {}

	authorize$(): Observable<void> {
		return combineLatest([
			this.store.select(this.sessionSelector.isAuthenticated()),
			this.store.select(JurisdictionSelector.get),
		]).pipe(
			switchMap(([isAuth, jurisdiction]) =>
				!isAuth && jurisdiction !== undefined ? this.kickOffNewLoginAttempt$(jurisdiction) : EMPTY
			),
			map(() => undefined)
		);
	}

	shouldShowConsents$(): Observable<void> {
		if (
			this.dataProtectionConfigService.getMarketingConsentDisabled() &&
			this.dataProtectionConfigService.getCookieConsentDisabled()
		) {
			return EMPTY;
		}
		return this.store.pipe(
			select(this.marketingSettingsSelector.hasGivenConsents()),
			filter((hasGivenConsents) => hasGivenConsents === false),
			withLatestFrom(
				this.store.select(
					CustomerManagementSelector.getPersistentData<boolean>(HIDE_GROUP_CONSENT_COUNTRY_OF_REGISTRATION)
				),
				this.store.select(this.cookieConsentSelector.isAccepted()),
				this.store.select(this.marketingSettingsSelector.isConsentsMarketingSettingsFilled()),
				this.store.select(this.sessionSelector.getConsentsNotAcceptedToken())
			),
			tap(([, hideGroupConsentCountry, cookieAccepted, isConsentsMarketingSettingsFilled, consentsToken]) => {
				const request: MarketingSettingsWithTokenRequest = {
					token: consentsToken!,
					consents: {
						thirdParty: true,
						...(cookieAccepted ? { cookie: true } : {}),
						groupMarketing: false,
					},
				};
				if (hideGroupConsentCountry) {
					this.store.dispatch(
						batchActions(
							this.marketingSettingsAction.updateMarketingSettingsWithToken(request),
							this.customerManagementAction.updatePersistentData(
								HIDE_GROUP_CONSENT_COUNTRY_OF_REGISTRATION,
								false
							)
						)
					);
				} else if (cookieAccepted && isConsentsMarketingSettingsFilled) {
					this.store.dispatch(this.marketingSettingsAction.updateMarketingSettingsWithToken(request));
					return;
				}
			}),
			filter(
				([, hideGroupConsentCountry, cookieAccepted, isConsentsMarketingSettingsFilled]) =>
					!(cookieAccepted && (isConsentsMarketingSettingsFilled || hideGroupConsentCountry))
			),
			map(() => undefined)
		);
	}

	acceptTermsAndConditionsFailed$(): Observable<Message[]> {
		return this.messageService.consume$({
			context: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.termsAndConditions,
			messageType: MessageType.Error,
		});
	}

	detectTermsAndConditions$(): Observable<void> {
		return this.detectLoginAgreementError$(AUTH_MESSAGE_CODES.login.termsAndConditionsNotAccepted);
	}

	detectLatestTermsAndConditions$(): Observable<void> {
		return this.detectLoginAgreementError$(AUTH_MESSAGE_CODES.login.latestTermsAndConditionsNotAccepted);
	}

	detectMigrationNotification$(): Observable<boolean> {
		return this.store.pipe(
			select(this.sessionSelector.isAuthenticated()),
			filter((isAuthenticated) => !!isAuthenticated),
			map(() => {
				const brandMigration = this.featureFlagService.get<BrandMigration | undefined>(
					"mga_to_tobique_jurisdiction"
				)?.tobiqueMigration;
				if (brandMigration?.tobiquePreMigrationIsEnabled) {
					// Getting brand migration date dynamically from Optimizely
					const { year, monthIndex, date, hours, minutes, seconds } = brandMigration.tobiqueMigrationDate;
					const migrationDate = new Date(Date.UTC(year, monthIndex, date, hours, minutes, seconds));
					const today = new Date();

					const timeDifferenceInMs = migrationDate.getTime() - today.getTime();
					const daysDifference = timeDifferenceInMs / (1000 * 3600 * 24);

					return daysDifference <= 30 && daysDifference > 0;
				}
				return false;
			})
		);
	}

	detectProfileNotCompleted$(): Observable<CustomerDataNotConfirmedResponse> {
		return this.messageService
			.consume$({
				context: [AUTH_MESSAGE_CONTEXT.login, AUTH_MESSAGE_CONTEXT.loginWithAuthenticationToken],
				messageType: MessageType.Error,
			})
			.pipe(
				filter(([error]) => error.messageCode === AUTH_MESSAGE_CODES.login.profileNotCompleted),
				map(([error]) => {
					return error.data as CustomerDataNotConfirmedResponse;
				})
			);
	}

	detectLatestTCandPP$(): Observable<LatestTCandPPNotAcceptedLoginResponseData> {
		return this.messageService
			.consume$({
				context: [AUTH_MESSAGE_CONTEXT.login, AUTH_MESSAGE_CONTEXT.loginWithAuthenticationToken],
				messageType: MessageType.Error,
			})
			.pipe(
				filter(([error]) => {
					const responseData = error.data as LatestTCandPPNotAcceptedResponse;
					return (
						(error.messageCode === AUTH_MESSAGE_CODES.login.latestTermsAndConditionsNotAccepted &&
							!!responseData.latestTcNotAcceptedLoginResponse) ||
						(error.messageCode === AUTH_MESSAGE_CODES.login.latestPrivacyPolicyNotAccepted &&
							!!responseData.latestPrivacyPolicyNotAcceptedLoginResponse)
					);
				}),
				map(([error]) => {
					const responseData = error.data as LatestTCandPPNotAcceptedResponse;
					if (error.messageCode === AUTH_MESSAGE_CODES.login.latestTermsAndConditionsNotAccepted) {
						return responseData.latestTcNotAcceptedLoginResponse as LatestTCandPPNotAcceptedLoginResponseData;
					}
					return responseData.latestPrivacyPolicyNotAcceptedLoginResponse as LatestTCandPPNotAcceptedLoginResponseData;
				})
			);
	}

	detectLatestPrivacyPolicy$(): Observable<void> {
		return this.detectLoginAgreementError$(AUTH_MESSAGE_CODES.login.latestPrivacyPolicyNotAccepted);
	}

	detectTwoFactorAuth$(): Observable<{
		challengeDetails: TwoFactorChallengeDetails | undefined;
		isAccountNotVerified: boolean;
	}> {
		return this.store.select(TwoFactorAuthSelector.selectChallengeDetails).pipe(
			withLatestFrom(this.genericLoginError$()),
			filter(([challengeDetails, messages]) => {
				const errorMessage = last(messages);

				return (
					challengeDetails?.challengeType === TwoFactorChallengeType.RegularAuthentication &&
					(errorMessage?.messageCode === AUTH_MESSAGE_CODES.login.twoFactorAuthenticationRequired ||
						(errorMessage?.messageCode === AUTH_MESSAGE_CODES.login.accountNotVerified &&
							this.twoFactorAuthConfigService.getPostRegistrationOTPEnabled()))
				);
			}),
			map(([challengeDetails, messages]) => ({
				challengeDetails,
				isAccountNotVerified: last(messages)?.messageCode === AUTH_MESSAGE_CODES.login.accountNotVerified,
			})),
			ignoreNil()
		);
	}

	suspiciousLogin$(): Observable<void> {
		return this.store.pipe(
			select(SuspiciousLoginSelector.getChallengeDetails),
			filter(
				(challengeDetails) => !!challengeDetails && !this.twoFactorAuthConfigService.getSuspiciousLoginEnabled()
			),
			map(() => undefined)
		);
	}

	newSuspiciousLogin$(): Observable<void> {
		return this.store.pipe(
			select(TwoFactorAuthSelector.selectChallengeDetails),
			filter((challenge) => challenge?.challengeType === TwoFactorChallengeType.SuspiciousLogin),
			map(() => undefined)
		);
	}

	detectEntityLegalNotice$(): Observable<void> {
		return this.detectLoginAgreementError$(AUTH_MESSAGE_CODES.login.latestEntityLegalNoticeNotAccepted);
	}

	genericLoginError$(): Observable<Message[]> {
		return this.messageService.consume$({
			context: [
				AUTH_MESSAGE_CONTEXT.login,
				AUTH_MESSAGE_CONTEXT.loginWithToken,
				AUTH_MESSAGE_CONTEXT.loginWithPersonalNumber,
				AUTH_MESSAGE_CONTEXT.loginWithTwoFactorAuthentication,
			],
			messageType: MessageType.Error,
			excludeCodes: SESSION_LOGIN_EXPIRED_CODES,
		});
	}

	sessionLimitExpired$(): Observable<SessionLimitExpiredMessage> {
		return this.messageService
			.consume$({
				context: [
					AUTH_MESSAGE_CONTEXT.login,
					AUTH_MESSAGE_CONTEXT.loginWithToken,
					AUTH_MESSAGE_CONTEXT.loginWithPersonalNumber,
					AUTH_MESSAGE_CONTEXT.loginWithTwoFactorAuthentication,
				],
				messageType: MessageType.Error,
			})
			.pipe(
				filter(([error]) => SESSION_LOGIN_EXPIRED_CODES.includes(error.messageCode)),
				map(([error]) => {
					const customerCanLogInAt =
						this.dateService.format(
							(error.data as SessionLimitExpiredMessage)?.customerCanLogInAt,
							"dd/MM/yyyy HH:mm"
						) || undefined;
					const code = error.messageCode;
					return {
						code,
						customerCanLogInAt,
					};
				})
			);
	}

	acceptTermsAndConditions$(): Observable<void> {
		return this.store.pipe(
			select(this.sessionSelector.getTermsAndConditionsToken()),
			filter((token) => !!token),
			tap((token) => this.store.dispatch(this.customerManagementAction.acceptTermsAndConditions(token!))),
			map(() => undefined)
		);
	}

	declineTermsAndConditions$(): Observable<void> {
		return this.store.pipe(
			select(this.sessionSelector.getTermsAndConditionsToken()),
			filter((token) => !!token),
			tap((token) => this.store.dispatch(this.customerManagementAction.declineTermsAndConditions(token!))),
			map(() => undefined)
		);
	}

	sgaLoginAlert$(): Observable<SgaLoginMessages> {
		return this.store.pipe(
			select(this.sessionSelector.isAuthenticated()),
			filter((isAuth) => isAuth),
			tap(() => this.store.dispatch(this.walletAction.getBetWinSummary(YEAR_IN_MINUTES))),
			switchMap(() =>
				combineLatest([
					this.store.select(CustomerManagementSelector.getCustomer),
					this.store.select(this.multiDepositLimitSelector.get()),
					this.store.select(this.walletSelector.getBetWinSummary()),
					this.store.select(this.multiSessionLimitSelector.get()),
				])
			),
			filter(([customer, , betWinSummary]) => !!betWinSummary && !!customer),
			take(1),
			map(([customer, multiDepositLimit, betWinSummary, multiSessionLimit]) => {
				let loginMessage = "";

				if (customer && customer.lastLogins.length > 1) {
					loginMessage = this.translationService.getByKey("sga-login-alert.login-message", {
						lastLoginDate: this.dateService.format(customer.lastLogins[1].timestamp, DateFormat.LongDate),
						lastLoginTime: this.dateService.format(customer.lastLogins[1].timestamp, DateFormat.ShortTime),
					});
				}

				const netWinning = this.currencyService.format(betWinSummary!.netWinning, undefined, " ");
				const netWinningMessage = this.translationService.getByKey("sga-login-alert.netWinningMessage", {
					netWinning: betWinSummary!.netWinning > 0 ? `+${netWinning}` : netWinning,
				});

				const messageCode = multiDepositLimit?.depositLimit
					? "sga-login-alert.deposit-limits-message"
					: "sga-login-alert.deposit-limits-not-set-message";
				const depositLimits = multiDepositLimit?.depositLimit
					? multiDepositLimit.depositLimit
					: {
							dailyDepositLimit: 0,
							weeklyDepositLimit: 0,
							monthlyDepositLimit: 0,
						};
				const depositLimitsMessage = this.translationService.getByKey(messageCode, {
					netWinningMessage,
					dailyLimit: depositLimits!.dailyDepositLimit,
					weeklyLimit: depositLimits!.weeklyDepositLimit,
					monthlyLimit: depositLimits!.monthlyDepositLimit,
				});

				let sessionLimitsMessage;
				const { dailySessionLimit, weeklySessionLimit, monthlySessionLimit } = multiSessionLimit?.current || {};
				if (dailySessionLimit && weeklySessionLimit && monthlySessionLimit) {
					sessionLimitsMessage = {
						text: this.translationService.getByKey("sga-login-alert.session-limits-message"),
						dailySessionLimit,
						weeklySessionLimit,
						monthlySessionLimit,
					};
				}

				let covid19limitMessage;
				if (this.covidMeasuresConfig.covidMeasuresEnabled() && depositLimits?.weeklyDepositLimit) {
					const threshold = this.depositLimitConfig.getDepositLimitExceedMessageThreshold();
					covid19limitMessage =
						this.depositLimitConfig.getDepositLimitExceedMessageEnabled() &&
						depositLimits?.weeklyDepositLimit > threshold
							? this.translationService.getByKey("sga-login-alert.deposit-max-limit-covid19-message", {
									threshold,
								})
							: undefined;
				}

				this.store.dispatch(this.messageAction.clearByContext(RESPONSIBLE_GAMING_MESSAGE_CONTEXT.loginAlert));
				return {
					loginMessage,
					netWinningMessage,
					depositLimitsMessage,
					covid19limitMessage,
					sessionLimitsMessage,
				};
			})
		);
	}

	dgaLoginAlert$(): Observable<DgaLoginMessages> {
		return this.store.pipe(
			select(this.sessionSelector.isAuthenticated()),
			filter((isAuth) => isAuth),
			switchMap(() => this.store.select(CustomerManagementSelector.getCustomer)),
			ignoreNil(),
			take(1),
			map((customer) => {
				const dgaLoginMessages: DgaLoginMessages = {};
				if (customer.lastLogins.length > 1) {
					dgaLoginMessages.lastLoginDate = this.dateService.format(
						customer.lastLogins[1].timestamp,
						DateFormat.LongDate
					);
					dgaLoginMessages.lastLoginTime = this.dateService.format(
						customer.lastLogins[1].timestamp,
						DateFormat.ShortTime
					);
				}
				this.store.dispatch(this.messageAction.clearByContext(RESPONSIBLE_GAMING_MESSAGE_CONTEXT.loginAlert));
				return dgaLoginMessages;
			})
		);
	}

	private detectLoginAgreementError$(messageCode: string): Observable<void> {
		return this.messageService
			.consume$({
				context: [
					AUTH_MESSAGE_CONTEXT.login,
					AUTH_MESSAGE_CONTEXT.loginWithToken,
					AUTH_MESSAGE_CONTEXT.loginWithPersonalNumber,
					AUTH_MESSAGE_CONTEXT.loginWithVerificationCode,
					AUTH_MESSAGE_CONTEXT.loginWithAuthenticationToken,
				],
				messageType: MessageType.Error,
				messageCode,
			})
			.pipe(map(() => undefined));
	}

	private kickOffNewLoginAttempt$(jurisdiction: Jurisdiction | undefined): Observable<any> {
		const verificationStatus$ = this.getVerificationStatus$(jurisdiction);

		return combineLatest([
			this.store.select(CustomerManagementSelector.isProfileUpdateSuccess),
			this.store.select(CustomerManagementSelector.hasAcceptedLatestTermsAndConditions),
			this.store.select(CustomerManagementSelector.hasAcceptedLatestPrivacyPolicy),
			this.store.select(CustomerManagementSelector.hasAcceptedEntityLegalNotice),
			this.store.select(this.marketingSettingsSelector.hasGivenConsents()),
			verificationStatus$,
		]).pipe(
			startWith([undefined, undefined, undefined, undefined, undefined, undefined] as [
				boolean | undefined,
				boolean | undefined,
				boolean | undefined,
				boolean | undefined,
				boolean | undefined,
				VerificationStatus | undefined,
			]),
			pairwise(),
			filter(
				([
					[
						prevProfileStatus,
						prevAcceptedTC,
						prevAcceptedPp,
						prevGivenConsents,
						prevAcceptedNotice,
						prevStatus,
					],
					[newProfileStatus, newAcceptedTC, newAcceptedPp, newGivenConsents, newAcceptedNotice, newStatus],
				]) =>
					// check if user accepted any of post login requirements
					(prevProfileStatus !== true && newProfileStatus === true) ||
					(prevAcceptedTC !== true && newAcceptedTC === true) ||
					(prevAcceptedPp !== true && newAcceptedPp === true) ||
					(prevGivenConsents !== true && newGivenConsents === true) ||
					(prevAcceptedNotice !== true && newAcceptedNotice === true) ||
					(prevStatus !== VerificationStatus.Completed && newStatus === VerificationStatus.Completed)
			),
			withLatestFrom(
				this.getAuthenticationToken$(jurisdiction),
				this.store.select(CustomerManagementSelector.getCredentials),
				this.store.select(RouteSelector.getCurrent),
				this.store.select(this.sessionSelector.getOneTimeLoginToken()),
				this.store.select(this.sessionSelector.getAuthenticationToken())
			),
			switchMap(
				([
					[[, , , , , prevStatus], [, , , , , newStatus]],
					loginWithToken,
					credentials,
					route,
					oneTimeLoginToken,
					authenticationToken,
				]) => {
					const token =
						loginWithToken ||
						(route?.queryParams?.token && route.queryParams.token.split(".").length === 3
							? route.queryParams.token
							: undefined);

					const loginWithTokenRequirements =
						jurisdiction === Jurisdiction.Dga
							? token && route?.name !== CORE_ROUTE.registration.register
							: this.authTransactionConfig.getLoginEnabled() && token;
					const termsAndConditionsWithTwoFactorAuthRequirements =
						this.twoFactorAuthConfigService.getTwoFactorAuthEnabled() && !!oneTimeLoginToken;

					const skipLogin =
						!!route &&
						route.name === CORE_ROUTE.registration.register &&
						((this.kycConfig.getIsSmartIdVerificationEnabled() &&
							this.matDialog.openDialogs.find((dialog) => dialog.id === SMART_ID_LOGIN_DIALOG_ID)) ||
							(this.authTransactionConfig.getBankIdSecureStartEnabled() &&
								prevStatus !== VerificationStatus.Completed &&
								newStatus === VerificationStatus.Completed));

					if (authenticationToken && !skipLogin) {
						return this.fraudPreventionService.getCustomerFraudToken$().pipe(
							tap((iovationBlackBox) => {
								const request: LoginWithAuthenticationToken = {
									authenticationToken,
									iovationBlackBox: iovationBlackBox?.sessionToken,
									fraudToken: this.groupIBFraudPreventionService.getSessionId(),
								};

								this.store.dispatch(this.sessionAction.loginWithAuthenticationToken(request));
							})
						);
					}

					if ((loginWithTokenRequirements && !skipLogin) || termsAndConditionsWithTwoFactorAuthRequirements) {
						return this.fraudPreventionService.getCustomerFraudToken$().pipe(
							tap((iovationBlackBox) => {
								this.store.dispatch(
									this.sessionAction.loginWithToken({
										token: termsAndConditionsWithTwoFactorAuthRequirements
											? oneTimeLoginToken
											: token,
										isOneTimeLoginPassword: termsAndConditionsWithTwoFactorAuthRequirements,
										iovationBlackBox: iovationBlackBox?.sessionToken,
										fraudToken: this.groupIBFraudPreventionService.getSessionId(),
									})
								);
							})
						);
					}

					if (oneTimeLoginToken && !skipLogin) {
						return this.fraudPreventionService.getCustomerFraudToken$().pipe(
							tap((iovationBlackBox) => {
								this.store.dispatch(
									this.sessionAction.loginWithToken({
										token: oneTimeLoginToken,
										isOneTimeLoginPassword: true,
										iovationBlackBox: iovationBlackBox?.sessionToken,
										fraudToken: this.groupIBFraudPreventionService.getSessionId(),
									})
								);
							})
						);
					}

					if (credentials) {
						this.store.dispatch(this.sessionAction.login(credentials));
					}
					return EMPTY;
				}
			)
		);
	}

	private getVerificationStatus$(jurisdiction: Jurisdiction | undefined): Observable<VerificationStatus | undefined> {
		switch (jurisdiction) {
			case Jurisdiction.Dga:
				return this.store.select(this.dgaAuthTransactionSelector.getLastStatus());
			case Jurisdiction.Mta:
			case Jurisdiction.Iaui:
				return this.store.select(SmartIdAuthTransactionSelector.getLastStatus);
			default:
				return this.store.select(this.authTransactionSelector.getLastStatus());
		}
	}

	private getAuthenticationToken$(jurisdiction: Jurisdiction | undefined): Observable<string | undefined> {
		switch (jurisdiction) {
			case Jurisdiction.Dga:
				return this.store.select(this.dgaAuthTransactionSelector.getAuthenticationToken());
			case Jurisdiction.Mta:
			case Jurisdiction.Iaui:
				return this.store.select(SmartIdAuthTransactionSelector.getAuthenticationToken);
			default:
				return this.store.select(this.authTransactionSelector.getAuthenticationToken());
		}
	}
}
